<template>
  <sp-card v-preserve-properties:elevation="1" class="age-group-notice">
    <div class="age-group-notice__content">
      <span class="age-group-notice__intro">
        <span class="age-group-notice__icon">
          <sp-icon name="age-group-all-ages-color"></sp-icon>
        </span>
        <span class="age-group-notice__text">{{ resultText("age_group_notice") }}</span>
      </span>
      <span class="age-group-notice__links">
        <sp-chip
          v-for="group in ageGroups"
          :key="group.slug"
          class="age-group-notice__group"
          variant="flat"
          color="primary"
          pill
        >
          <sp-icon slot="prepend" class="age-group-notice__group-icon" :name="group.icon"></sp-icon>
          <a
            class="age-group-notice__group-link"
            :href="`?age_group=${encodeURIComponent(group.slug)}`"
            :data-age-group="group.slug"
            :data-testid="TestId.finder.ageGroupNoticeChoice"
            @click="handleAddAgeGroup"
            >{{ group.label }}</a
          >
        </sp-chip>
      </span>
    </div>
  </sp-card>
</template>

<script setup>
import { TestId } from "#/test-ids";
import { resultText } from "#ce/components/SpFinder/view-models/result-text";
import { vPreserveProperties } from "#ce/utils/property-bindings";

defineProps({
  /**
   * The age groups that narrow the results: `{ slug, label, icon }`.
   *
   * @type {Array}
   */
  ageGroups: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(["add-filters"]);

// A modified or non-primary click keeps the link's own behaviour, such as opening a new tab.
function handleAddAgeGroup(event) {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  event.preventDefault();
  emit("add-filters", { age_group: event.currentTarget.dataset.ageGroup });
}
</script>

<style lang="scss" scoped>
@use "@assets/mixins/breakpoint" as *;

.age-group-notice {
  --age-groups-padding: var(--sp-comp-finder-results-age-groups-padding-mobile, 25px 36px 32px);
  --age-groups-padding-desktop: var(--sp-comp-finder-results-age-groups-padding, 20px);
  --age-groups-intro-font: var(--sp-comp-finder-results-age-groups-intro-font, var(--sp-sys-typescale-body-bold-short));

  --sp-comp-card-padding-block: 0;
  --sp-comp-card-padding-inline: 0;

  display: block;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(--age-groups-padding);

    @include sp-breakpoint(sm) {
      flex-direction: row;
      padding: var(--age-groups-padding-desktop);
    }
  }

  &__intro {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    align-items: center;
    margin-bottom: 20px;
    text-align: center;

    @include sp-breakpoint(sm) {
      flex-direction: row;
      margin-bottom: 0;
      text-align: start;
    }
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    margin-bottom: 15px;
    border-radius: 50%;
    background-color: #e4f0f2;

    --sp-comp-icon-size: 37px;

    @include sp-breakpoint(sm) {
      margin-right: 20px;
      margin-bottom: 0;
    }
  }

  &__text {
    font: var(--age-groups-intro-font);
    font-size: 14px;
  }

  &__links {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
  }

  &__group {
    --sp-comp-chip-padding-inline: 15px;
    --sp-comp-chip-padding-block: 10px;
    --sp-comp-chip-gap: 5px;
    --sp-comp-chip-font-size: 12px;

    @include sp-breakpoint(sm) {
      --sp-comp-chip-font-size: 14px;
    }
  }

  &__group-icon {
    --sp-comp-icon-size: 20px;
    --sp-comp-icon-fill: var(--sp-sys-color-on-primary);
  }

  &__group-link {
    color: var(--sp-sys-color-on-primary);
    font-weight: inherit;
    text-decoration: none;
  }
}
</style>
